import { createFeatureSelector, createSelector } from '@ngrx/store';
import { memoize } from 'lodash';
import { GenesysCloudEmailConversationsState } from '../genesys-cloud-email-conversations.state';
import { featureKey } from '../reducers/feature-key';
import { emailConversationEntityAdapter } from '../adapters/email-conversation.adapter';
import { Participant } from 'common/store/genesys-cloud/conversations/common/models/participant.model';
import { ConnectionState } from 'common/store/genesys-cloud/conversations/common/models/connection-state.enum';
import { Email } from 'common/store/genesys-cloud/conversations/emails/models/email.model';
import { wrapupRequiredValues } from 'common/store/genesys-cloud/conversations/common/helpers/wrap-up-required-values';

const { selectAll, selectEntities, selectIds } = emailConversationEntityAdapter.getSelectors();
export const selectState = createFeatureSelector<GenesysCloudEmailConversationsState>(featureKey);
const selectWrapUpsSubState = createSelector(selectState, (state) => state.wrapups);
const selectConversationsSubState = createSelector(selectState, (state) => state.conversations);
const selectAllEmailConversations = createSelector(selectConversationsSubState, selectAll);
const selectConversationEntities = createSelector(selectConversationsSubState, selectEntities);
export const selectAllEmailConversationIds = createSelector(
  selectConversationsSubState,
  (subState) => <string[]>selectIds(subState)
);

export const selectActiveEmailConversations = createSelector(
  selectAllEmailConversations,
  (conversations) => conversations?.filter((c) => c.isActive) || []
);

export const selectActiveEmailConversationCount = createSelector(
  selectActiveEmailConversations,
  (conversations) => conversations.length
);

export const selectEmailConversationById = memoize((conversationId: string) =>
  createSelector(selectConversationEntities, (entities) => entities[conversationId])
);

export const selectWrapUpsByConversationId = memoize((conversationId: string) =>
  createSelector(selectWrapUpsSubState, (entities) => entities[conversationId] || [])
);

const selectAgentParticipant = memoize((conversationId: string) =>
  createSelector(selectEmailConversationById(conversationId), (conversation) => conversation?.agentParticipant)
);

export const selectEmailByMessageId = memoize((conversationId: string, messageId: string) =>
  createSelector(selectAgentParticipant(conversationId), (agentParticipant) => ({
    email: agentParticipant?.emails.find((email) => email.messageId === messageId),
    conversationId
  }))
);

const selectCustomerParticipant = memoize((conversationId: string) =>
  createSelector(selectEmailConversationById(conversationId), (conversation) => conversation?.customerParticipant)
);

export const selectIsConversationAcceptable = memoize((conversationId: string) =>
  createSelector(selectEmailConversationById(conversationId), (conversation) => conversation?.isAcceptable)
);

export const selectCommunicationId = memoize((conversationId: string) =>
  createSelector(selectAgentParticipant(conversationId), getCommunicationId)
);

export const selectWrapupPrompt = memoize((conversationId: string) =>
  createSelector(selectEmailConversationById(conversationId), (conversation) =>
    wrapupRequiredValues.has(conversation.agentParticipant?.wrapupPrompt)
  )
);

export const selectEmailError = memoize((conversationId: string) =>
  createSelector(
    selectAgentParticipant(conversationId),
    (agentParticipant) =>
      agentParticipant?.emails?.find((e) => e.state === ConnectionState.Connected && e.errorInfo)?.errorInfo?.message
  )
);

export const selectHasEmailError = memoize((conversationId: string) =>
  createSelector(selectAgentParticipant(conversationId), hasEmailError)
);

export const selectCustomerEmailAddresses = memoize((conversationId: string) =>
  createSelector(selectCustomerParticipant(conversationId), (customerParticipant) =>
    (customerParticipant?.address || '')
      .split(',')
      .filter((address) => address)
      .map((address) => ({ email: address }))
  )
);

export const selectAgentparticipantEmails = memoize((conversationId: string) =>
  createSelector(selectAgentParticipant(conversationId), (agentParticipant) => agentParticipant?.emails)
);

export const selectIsConversationOpen = memoize((conversationId: string) =>
  createSelector(selectAgentparticipantEmails(conversationId), (emails) =>
    emails?.some((e) => e.state !== ConnectionState.Disconnected)
  )
);

export const selectIsConversationTransmitting = memoize((conversationId: string) =>
  createSelector(selectAgentparticipantEmails(conversationId), (emails) =>
    emails?.some((e) => e.state === ConnectionState.Transmitting)
  )
);

function hasCommunicationId(email: Email): boolean {
  return (
    email.state.toUpperCase() === ConnectionState.Connected.toUpperCase() ||
    email.state.toUpperCase() === ConnectionState.Disconnected.toUpperCase()
  );
}

function getCommunicationId(agentParticipant: Participant): string {
  return agentParticipant?.emails.find((e) => hasCommunicationId(e) && !e.wrapup)?.id;
}

function hasEmailError(agentParticipant: Participant): boolean {
  return agentParticipant?.emails?.some((e) => e.state === ConnectionState.Connected && !!e.errorInfo);
}
